import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Lock, PackageCheck, Repeat } from 'lucide-react';
import type { OrderItem, PayableStage } from '@tailoring-crm/shared';
import { ItemAssignments } from '../components/assignments/item-assignments.js';
import { InvoiceCard } from '../components/invoices/invoice-card.js';
import { StageHistoryList } from '../components/kanban/stage-history.js';
import { OrderUpdatesCard } from '../components/orders/order-updates-card.js';
import { ProductLinesCard } from '../components/orders/product-lines-card.js';
import { PaymentsCard } from '../components/payments/payments-card.js';
import { SourcingCard } from '../components/sourcing/sourcing-card.js';
import { CustomerDesignsBand } from '../components/design-board/customer-designs-band.js';
import { DesignBoardPanel } from '../components/design-board/design-board-panel.js';
import { BookAlterationDialog } from '../components/alterations/book-alteration-dialog.js';
import { MeasurementSketchDialog } from '../components/measurements/measurement-sketch-dialog.js';
import { SketchPicture } from '../components/measurements/sketch-picture.js';
import { PageHeader } from '../components/layout/page-header.js';
import { useTemplates } from '../lib/measurements/use-templates.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import {
  useAdjustItemMeasurements,
  useCancelOrder,
  useOrder,
  useRepeatOrder,
} from '../lib/orders/use-orders.js';
import { useAssignments } from '../lib/assignments/use-assignments.js';
import { useDeliverOrder, useStageConfiguration } from '../lib/workflow/use-kanban.js';
import { useSession } from '../lib/session/session-context.js';
import { formatCurrency, formatDate, formatDateLong, formatMobile } from '../lib/format.js';

/**
 * One order, garment by garment.
 *
 * The part worth reading is the measurements block on each garment. It shows the FROZEN copy
 * this order was booked against, says so in as many words, and offers to adjust THIS garment
 * — never the customer's saved profile. A shop that wants one sleeve an inch longer must not
 * be able to reach the customer's profile from here, because doing so would corrupt every
 * future order to fix one present one.
 */
export function OrderDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { orderId = '' } = useParams();

  const order = useOrder(orderId || null);
  const repeat = useRepeatOrder(orderId);
  const cancel = useCancelOrder(orderId);
  const deliver = useDeliverOrder();
  const configuration = useStageConfiguration();
  const session = useSession();
  // One request for the whole bill, sliced per garment below.
  const assignments = useAssignments({ orderId }, { enabled: orderId !== '' });

  // Handing work out and agreeing a different rate are two permissions, and a manager holds
  // both — but a tailor holds neither, and never reaches this page at all.
  const canAssign = session?.role !== 'tailor';
  const stageLabel = (stage: PayableStage): string =>
    configuration.data?.stages.find((one) => one.key === stage)?.label ?? t(`stage.${stage}`);

  const [adjusting, setAdjusting] = useState<OrderItem | null>(null);
  /** The garment being booked back in, or null while nobody is. */
  const [altering, setAltering] = useState<OrderItem | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  if (order.isPending) {
    return (
      <>
        <PageHeader title={t('orders.title')} />
        <ListSkeleton rows={4} />
      </>
    );
  }

  const data = order.data;
  if (!data) {
    return (
      <>
        <PageHeader title={t('orders.title')} description={t('error.unexpected')} />
        <Button asChild variant="outline">
          <Link to="/orders">{t('orders.title')}</Link>
        </Button>
      </>
    );
  }

  /** A failure here is the shop's to hear about, not the console's. */
  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const onRepeat = async (): Promise<void> => {
    try {
      const created = await repeat.mutateAsync(null);
      toast({ title: t('orders.repeated', { number: created.orderNumber }), variant: 'success' });
      navigate(`/orders/${created.id}`);
    } catch (error) {
      report(error);
    }
  };

  /**
   * "The customer collected the order."
   *
   * Every garment that is READY goes at once; anything still being made is left where it is and
   * counted back, because a bill marked delivered while two blouses sit on the table is exactly
   * the dispute the workflow exists to prevent.
   */
  const onHandOver = async (): Promise<void> => {
    try {
      const result = await deliver.mutateAsync({ orderId });
      toast({
        title: t('orders.handedOver', { count: result.moved.length }),
        ...(result.skipped.length > 0
          ? { description: t('orders.notReadyYet', { count: result.skipped.length }) }
          : {}),
        variant: result.moved.length > 0 ? 'success' : 'default',
      });
    } catch (error) {
      report(error);
    }
  };

  const onCancel = async (): Promise<void> => {
    try {
      await cancel.mutateAsync(cancelReason);
      toast({ title: t('orders.cancelled', { number: data.orderNumber }), variant: 'success' });
      setCancelling(false);
      setCancelReason('');
    } catch (error) {
      report(error);
    }
  };

  return (
    <>
      <PageHeader
        title={t('orders.detailTitle', { number: data.orderNumber })}
        description={`${data.customerName} · ${formatMobile(data.customerPhone)}`}
        action={
          <div className="flex flex-wrap gap-2">
            {!data.cancelledAt && data.stage !== 'delivered' ? (
              <Button disabled={deliver.isPending} onClick={() => void onHandOver()}>
                <PackageCheck aria-hidden="true" />
                {t('orders.handOver')}
              </Button>
            ) : null}
            {/* "Same again" repeats garments; a counter sale has none to repeat. */}
            {data.items.length > 0 ? (
              <Button variant="outline" disabled={repeat.isPending} onClick={() => void onRepeat()}>
                <Repeat aria-hidden="true" />
                {t('orders.repeat')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="flex flex-col gap-5">
        <Link
          to="/orders"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('orders.title')}
        </Link>

        <section className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{t(`stage.${data.stage}`)}</Badge>
          {data.priority ? <Badge variant="destructive">{t('orders.urgent')}</Badge> : null}
          {data.cancelledAt ? <Badge variant="warning">{t('orders.cancelledBadge')}</Badge> : null}
          <span className="text-sm text-muted-foreground">
            {t('orders.bookedOn', { date: formatDateLong(data.bookedAt) })}
          </span>
        </section>

        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div className="rounded-lg border border-border p-3">
            <dt className="text-xs text-muted-foreground">{t('orders.promisedDate')}</dt>
            <dd className="font-semibold">
              {data.promisedDate ? formatDate(data.promisedDate) : t('orders.noPromisedDate')}
            </dd>
          </div>
          <div className="rounded-lg border border-border p-3">
            <dt className="text-xs text-muted-foreground">{t('orders.total')}</dt>
            <dd className="font-semibold tabular-nums">{formatCurrency(data.totalPaise)}</dd>
          </div>
          <div className="rounded-lg border border-border p-3">
            <dt className="text-xs text-muted-foreground">{t('orders.paid')}</dt>
            <dd className="font-semibold tabular-nums">{formatCurrency(data.amountPaidPaise)}</dd>
          </div>
          <div className="rounded-lg border border-border p-3">
            <dt className="text-xs text-muted-foreground">{t('orders.balance')}</dt>
            <dd className="font-semibold tabular-nums">
              {data.balancePaise > 0 ? formatCurrency(data.balancePaise) : t('orders.settled')}
            </dd>
          </div>
        </dl>

        {data.notes ? <p className="text-sm text-muted-foreground">{data.notes}</p> : null}

        {/* The money, above the garments: a customer standing at the counter to collect is
            there to pay, and the balance is the number the shop is looking for. */}
        <PaymentsCard order={data} />

        {/* The bill, directly under the money: a customer collecting is paying and asking for
            their bill in the same breath, and the two belong on the same part of the screen. */}
        <InvoiceCard order={data} />

        {/* What still has to be bought before this can be made. Under the money because it is the
            other half of the same question — the bill says what the customer owes, this says what
            the shop still has to spend to earn it. */}
        <SourcingCard orderId={orderId} orderNumber={data.orderNumber} items={data.items} />

        {/* What the customer has already been told, and how to tell them something now. Above
            the garments because it is asked with a customer on the phone. */}
        <OrderUpdatesCard order={data} />

        {/* What she approved before this was booked, read from her own record rather than copied
            onto this one. Above the garments because it is what they are being cut from. */}
        <CustomerDesignsBand customerId={data.customerId} customerName={data.customerName} />

        {/*
          THIS booking's own board, directly under the band that shows what came before it — the
          two answer different questions and are next to each other so the difference is visible.
          The band above is her record, read-only and older than this order; this is the working
          surface for the thing being made now, and a card added here can be drawn by hand rather
          than photographed.

          An order board is not a substitute for her board. A neckline argued out before there was
          a booking belongs to the person; a change agreed at the fitting for THIS lehenga belongs
          to the order, which is what the tailor cuts from. The API has carried both owners since
          the board stopped being an order's alone — only this screen was missing.

          `order` is passed so a tile can name the garment its card is about; without it a
          two-garment booking shows two sketches and no way to tell which is which.
        */}
        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-sm font-semibold">{t('designBoard.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('designBoard.thisOrderDescription')}</p>
          </div>
          {/* A tailor may read a board and not write one (`design_cards.write` is theirs to
              neither hold nor be offered), so the controls are gated on the same rule the rest of
              this page uses rather than left to fail at the API. */}
          <DesignBoardPanel
            owner={{ kind: 'order', orderId }}
            order={data}
            readOnly={session?.role === 'tailor'}
          />
        </section>

        {/* What was sold over the counter. Above the garments, because on a counter sale it is
            the whole of the bill, and on a mixed one it is short. */}
        <ProductLinesCard order={data} canEdit={session?.role !== 'tailor'} />

        {data.items.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-muted-foreground">
              {t('orders.garmentsHeading')}
            </h3>

            <ul className="flex flex-col gap-3">
              {data.items.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-col gap-3 rounded-lg border border-border p-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="flex flex-wrap items-center gap-2 font-medium">
                        {item.quantity} × {item.garmentTypeLabel}
                        <Badge variant="secondary">{t(`stage.${item.stage}`)}</Badge>
                        {item.cancelledAt ? (
                          <Badge variant="warning">{t('orders.cancelledBadge')}</Badge>
                        ) : null}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {item.promisedDate
                          ? formatDate(item.promisedDate)
                          : t('orders.noPromisedDate')}
                        {' · '}
                        {item.fabric.source === 'shop'
                          ? t('orders.fabricShop')
                          : t('orders.fabricCustomer')}
                      </p>
                    </div>
                    <p className="font-semibold tabular-nums">{formatCurrency(item.linePaise)}</p>
                  </div>

                  {item.notes ? (
                    <p className="text-sm text-muted-foreground">{item.notes}</p>
                  ) : null}

                  {/* The frozen snapshot. Said plainly, because the guarantee is the feature. */}
                  <div className="flex flex-col gap-2 rounded-md bg-muted/50 p-3">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                      <Lock aria-hidden="true" className="size-3.5" />
                      {t('orders.measurementsHeading')}
                      {item.measurementsAdjusted ? (
                        <Badge variant="warning">{t('orders.measurementsAdjusted')}</Badge>
                      ) : null}
                    </p>

                    {item.measurements ? (
                      <>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                          {item.measurements.values.map((value) => (
                            <div key={value.fieldKey} className="flex justify-between gap-2">
                              <dt className="text-muted-foreground">{value.label}</dt>
                              <dd className="font-medium tabular-nums">{value.display}</dd>
                            </div>
                          ))}
                        </dl>
                        <ItemSketch
                          orderId={orderId}
                          item={item}
                          canRedraw={session?.role !== 'tailor' && !item.cancelledAt}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('orders.measurementsFrozen')}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="self-start"
                          onClick={() => setAdjusting(item)}
                        >
                          {t('orders.adjustMeasurements')}
                        </Button>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('orders.noMeasurements')}</p>
                    )}
                  </div>

                  {/* Booking it back in. Offered at any stage — a customer at a trial fitting
                    who wants the sleeve narrower is asking for an alteration too, and the API
                    has always accepted one — but NOT on a cancelled garment, which was never
                    made. It creates a record of its own and leaves this order, its dates and
                    its stage history exactly as they are. */}
                  {!item.cancelledAt ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="self-start"
                      onClick={() => setAltering(item)}
                    >
                      {t('alterations.bookAction')}
                    </Button>
                  ) : null}

                  {/* Who is stitching it, and everyone it has been through. Above the photographs
                    because "whose is this?" is asked far more often than "what did it look
                    like?" once an order is in production. */}
                  {!item.cancelledAt ? (
                    <ItemAssignments
                      itemId={item.id}
                      rows={assignments.data?.items ?? []}
                      garmentLabel={`${item.quantity} × ${item.garmentTypeLabel}`}
                      itemStage={item.stage}
                      stageLabel={stageLabel}
                      canAssign={canAssign}
                      canOverrideRate={canAssign}
                    />
                  ) : null}

                  {/* Loaded only when opened — most garments are never asked about, and the ones
                    that are, are asked about in an argument. */}
                  <details className="rounded-md border border-border p-3">
                    <summary className="cursor-pointer text-sm font-semibold">
                      {t('orders.historyHeading')}
                    </summary>
                    <div className="pt-3">
                      <StageHistoryList orderId={orderId} itemId={item.id} />
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {!data.cancelledAt ? (
          <Button variant="destructive" className="self-start" onClick={() => setCancelling(true)}>
            {t('orders.cancelOrder')}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">{data.cancelReason}</p>
        )}
      </div>

      {adjusting ? (
        <AdjustMeasurementsDialog
          orderId={orderId}
          item={adjusting}
          onClose={() => setAdjusting(null)}
        />
      ) : null}

      {/* Cancelling asks for a reason and will not proceed without one — the argument this
          field exists to end is "why was this cancelled?" a month later. */}
      <Dialog open={cancelling} onOpenChange={setCancelling}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.cancelTitle', { number: data.orderNumber })}</DialogTitle>
            <DialogDescription>{t('orders.cancelReasonHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cancel-reason">{t('orders.cancelReasonLabel')}</Label>
            <Input
              id="cancel-reason"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={cancelReason.trim().length < 3 || cancel.isPending}
              onClick={() => void onCancel()}
            >
              {t('orders.cancelConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BookAlterationDialog
        open={altering !== null}
        onOpenChange={(next) => {
          if (!next) setAltering(null);
        }}
        item={altering}
      />
    </>
  );
}

/**
 * The drawing frozen with this garment's measurements, and "Redraw for this order".
 *
 * A redraw saves a NEW sketch and points only this item's copy at it — through the same adjust
 * endpoint as an inch let out, so the customer's own sketch (and every other order that froze it)
 * is out of reach from here, exactly as her numbers are.
 */
function ItemSketch({
  orderId,
  item,
  canRedraw,
}: {
  orderId: string;
  item: OrderItem;
  canRedraw: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const adjust = useAdjustItemMeasurements(orderId);
  const templates = useTemplates({ enabled: canRedraw });
  const [drawing, setDrawing] = useState(false);

  const sketch = item.measurements?.sketch ?? null;
  if (!sketch && !canRedraw) return null;

  const outline =
    templates.data?.templates.find((one) => one.garmentTypeKey === item.garmentTypeKey)?.outline ??
    null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {sketch ? (
        <SketchPicture
          previewMediaId={sketch.previewMediaId}
          name={item.measurements?.profileName ?? item.garmentTypeLabel}
        />
      ) : null}
      {canRedraw ? (
        <div className="flex flex-col gap-1">
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            disabled={adjust.isPending}
            onClick={() => setDrawing(true)}
          >
            {sketch ? t('measurementSketch.redrawForOrder') : t('measurementSketch.drawForOrder')}
          </Button>
          <p className="max-w-xs text-xs text-muted-foreground">
            {t('measurementSketch.redrawForOrderHint')}
          </p>
        </div>
      ) : null}

      {drawing ? (
        <MeasurementSketchDialog
          garmentTypeKey={item.garmentTypeKey}
          name={item.measurements?.profileName ?? item.garmentTypeLabel}
          sketchId={sketch?.sketchId ?? null}
          outlineSketchId={outline?.sketchId ?? null}
          onSaved={(saved) => {
            void adjust
              .mutateAsync({ itemId: item.id, input: { sketchId: saved.sketchId } })
              .then(() => toast({ title: t('orders.adjusted'), variant: 'success' }))
              .catch((error: unknown) =>
                toast({
                  title: error instanceof Error ? error.message : t('error.unexpected'),
                  variant: 'destructive',
                }),
              );
          }}
          onClose={() => setDrawing(false)}
        />
      ) : null}
    </div>
  );
}

/** Adjusts one garment's frozen copy. The customer's profile is not reachable from here. */
function AdjustMeasurementsDialog({
  orderId,
  item,
  onClose,
}: {
  orderId: string;
  item: OrderItem;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const adjust = useAdjustItemMeasurements(orderId);

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (item.measurements?.values ?? []).map((value) => [
        value.fieldKey,
        value.value !== null ? String(value.value) : (value.text ?? ''),
      ]),
    ),
  );

  const submit = async (): Promise<void> => {
    const original = new Map(
      (item.measurements?.values ?? []).map((value) => [
        value.fieldKey,
        value.value !== null ? String(value.value) : (value.text ?? ''),
      ]),
    );
    const adjustments = Object.entries(values)
      .filter(([fieldKey, entered]) => original.get(fieldKey) !== entered)
      .map(([fieldKey, entered]) => ({ fieldKey, enteredAs: entered.trim() || null }));

    if (adjustments.length === 0) {
      onClose();
      return;
    }

    await adjust.mutateAsync({ itemId: item.id, input: { adjustments } });
    toast({ title: t('orders.adjusted'), variant: 'success' });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('orders.adjustTitle', { garment: item.garmentTypeLabel })}</DialogTitle>
          <DialogDescription>{t('orders.adjustHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {(item.measurements?.values ?? []).map((value) => (
            <div key={value.fieldKey} className="flex flex-col gap-1.5">
              <Label htmlFor={`adjust-${value.fieldKey}`}>
                {value.label}
                <span className="ml-1 font-normal text-muted-foreground">({value.unit})</span>
              </Label>
              <Input
                id={`adjust-${value.fieldKey}`}
                inputMode="decimal"
                value={values[value.fieldKey] ?? ''}
                onChange={(event) =>
                  setValues((previous) => ({ ...previous, [value.fieldKey]: event.target.value }))
                }
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={adjust.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OrderDetailPage;
